import { ReactNode } from 'react';

import type { MetricsBoxProps } from '@cast/design-system';
import { ChartMetric } from '@cast/features-cluster-ui';
import { Money } from '@cast/split-me/components';
import { dashesOrNum } from '@cast/utils';

type InventoryResourceMetricBoxProps<TKey extends string> = {
  dataKey: TKey;
  value?: number | null | undefined;
  type?: 'number' | 'currency';
  precision?: number;
  fixed?: number;
  unit?: ReactNode;
  testId?: string;
  colorMap: Record<TKey, string | undefined>;
  titleMap: Record<TKey, string>;
  toggleMetric?: (key: TKey) => void;
  isMetricDisabled?: (key: TKey) => boolean;
  size?: MetricsBoxProps['size'];
  label?: string;
  infoTooltip?: ReactNode;
  dashed?: boolean;
  dashedColor?: string;
  roundBy?: number;
  decimals?: number;
};

export const InventoryResourceMetricBox = <TKey extends string>({
  dataKey,
  value,
  type = 'number',
  precision = 2,
  fixed = 2,
  unit,
  testId,
  colorMap,
  titleMap,
  toggleMetric,
  isMetricDisabled,
  size = 's',
  label,
  infoTooltip,
  dashed,
  dashedColor,
  roundBy = 3,
  decimals = 1,
}: InventoryResourceMetricBoxProps<TKey>) => {
  const metricColor = colorMap[dataKey];

  return (
    <ChartMetric
      testId={testId}
      size={size}
      label={label ?? titleMap[dataKey]}
      infoTooltip={infoTooltip}
      value={
        type === 'currency' && value != null ? (
          <Money amount={value} precision={precision} fixed={fixed} />
        ) : (
          dashesOrNum({ value, roundBy, decimals })
        )
      }
      unit={unit}
      color={dashed ? undefined : metricColor}
      dashedMarker={dashed}
      dashedColor={dashed ? dashedColor ?? metricColor : undefined}
      disabled={isMetricDisabled?.(dataKey) ?? false}
      onClick={toggleMetric ? () => toggleMetric(dataKey) : undefined}
    />
  );
};
